import React from "react";

import { faBookOpenCover } from "@performyard/fontawesome/pro-regular-svg-icons";
import { Icon, Skeleton } from "@performyard/py-components";
import type { TemplateLibraryEntrySummary } from "@performyard/service-contracts";

import { TemplateLibraryListItem } from "./TemplateLibraryListItem";

type Props = {
	entries: TemplateLibraryEntrySummary[] | undefined;
	loading: boolean;
	selectedId: string | null;
	onSelect: (id: string) => void;
	query?: string;
};

export const TemplateLibraryList = ({ entries, loading, selectedId, onSelect, query }: Props) => {
	if (loading) {
		return (
			<div className="space-y-2 p-3">
				{[0, 1, 2, 3].map((ii) => (
					<div key={ii} className="flex items-start gap-3 rounded-md border border-neutral-2 p-4">
						<Skeleton className="h-8 w-8 rounded" />
						<div className="flex-1 space-y-2">
							<Skeleton className="h-4 w-3/4 rounded" />
							<Skeleton className="h-3 w-11/12 rounded" />
							<Skeleton className="h-3 w-2/5 rounded" />
						</div>
					</div>
				))}
			</div>
		);
	}

	if (!entries || entries.length === 0) {
		return (
			<div className="flex min-h-[480px] flex-col items-center justify-center px-8 py-16 text-center">
				<div className="mb-4 flex size-12 items-center justify-center rounded-full bg-primary-1">
					<Icon icon={faBookOpenCover} color="primary-7" size={24} />
				</div>
				<div className="text-paragraph-1 font-semi-bold text-neutral-9">
					{query ? "Nothing matches that search" : "The library is empty"}
				</div>
				<div className="mt-1 max-w-[260px] text-paragraph-3 text-neutral-7">
					{query
						? "Try a different keyword or clear the filter to see everything."
						: "When templates are published they'll appear here for one-click apply."}
				</div>
			</div>
		);
	}

	return (
		<div
			className="max-h-[760px] divide-y divide-neutral-2 overflow-y-auto lg:absolute lg:inset-0 lg:max-h-none"
			role="listbox"
			aria-label="Template library entries"
		>
			{entries.map((entry) => {
				const id = String(entry._id);
				return (
					<TemplateLibraryListItem key={id} entry={entry} selected={id === selectedId} onSelect={onSelect} />
				);
			})}
		</div>
	);
};

export default TemplateLibraryList;
